import React, { useMemo } from 'react';

import { IAbstractRecord } from '@models';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  createFilterOptions,
  FormControl,
  TextField
} from '@mui/material';
import { MAIN_THEME } from '@styles';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { useAppSelector } from 'src/core/redux/hooks';
import { GlobalLovInjector } from 'src/core/redux/lovs/types';
import { makeStyles } from 'tss-react/mui';

interface IMultiselectFormFieldBase extends IFormFieldInputBaseProps {
  values: string[];
  hideError?: boolean;
  loader?: boolean;
  maxTagCount?: number;
  freeSolo?: boolean;
  onBlurSaveNewData?: boolean;
  onChange?: (value: string[]) => void;
  onBlur?: (
    extraValue: string[],
    e?: React.FocusEvent<HTMLDivElement, Element>
  ) => void;
  onFocus?: () => void;
  classes?: { input?: string; wrapper?: string };
  showSelectAll?: boolean;
  hidden?: boolean;
  disabledIds?: string[];
}

// 1) If `injectGlobalLov` is not provided, `selectOptions` is required.
interface IMultiselectFormFieldWithLocalOptions
  extends IMultiselectFormFieldBase {
  selectOptions: Record<string, string>;
  injectGlobalLov?: never;
}

// 2) If `injectGlobalLov` is provided, `selectOptions` is not allowed.
interface IMultiselectFormFieldWithGlobalLov extends IMultiselectFormFieldBase {
  injectGlobalLov: GlobalLovInjector;
  selectOptions?: never;
}

export type IMultiselectFormFieldProps =
  | IMultiselectFormFieldWithLocalOptions
  | IMultiselectFormFieldWithGlobalLov;

const useStyles = makeStyles<{
  error?: string;
  material?: boolean;
  canSelectAll?: boolean;
  canClearAll?: boolean;
}>()((theme, { error, canSelectAll, canClearAll }) => ({
  inputContainerStyles: {
    width: '100%',
    marginBottom: '5px',
    fontFamily: MAIN_THEME.font.regular
  },
  fieldArea: {
    flex: 1,
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative'
  },
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      }
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      fontFamily: MAIN_THEME.font.regular,
      lineHeight: '15px',
      minHeight: '34px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    }
  },
  chipMaxTagDesign: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    height: 'unset',
    color: MAIN_THEME.color.black47,
    marginRight: '10px',
    backgroundColor: MAIN_THEME.color.white92,
    padding: '0 13px',
    borderRadius: '6px'
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular
  },
  inputLabelStyles: { marginBottom: '5px' },
  inputErrorStyles: {
    minHeight: '25px',
    color: MAIN_THEME.color.red,
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.xs,
    marginTop: '5px'
  },
  inputRedBorder: {
    border: `1px solid ${MAIN_THEME.color.red}`
  },
  chipStyle: {
    height: 'unset',
    color: MAIN_THEME.color.white,
    backgroundColor: MAIN_THEME.color.black,
    borderRadius: '0px',
    margin: '3px',
    '& .MuiChip-deleteIcon': { color: 'white !important' }
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '10px',
    opacity: 1,
    height: '25px',
    width: '25px',
    fontFamily: MAIN_THEME.font.regular
  },
  selectMaterial: {
    '& .MuiInput-input': {
      fontFamily: MAIN_THEME.font.regular,
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      color: MAIN_THEME.color.black25
    },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset',
      borderRadius: '0',
      borderBottom: `1px solid ${MAIN_THEME.color.white92}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${MAIN_THEME.color.white92}`
      }
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${theme.palette.error.main}`
      },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`
    }
  },
  actionsInline: {
    display: 'flex',
    gap: '8px',
    marginLeft: '8px',
    whiteSpace: 'nowrap'
  },
  actionsBelow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '6px',
    gap: '8px'
  },
  actionButton: {
    textDecoration: 'none',
    border: 0,
    outline: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    color: MAIN_THEME.color.black,
    padding: 0,
    '&:hover': { opacity: 0.7 }
  },
  selectAllButton: {
    pointerEvents: canSelectAll ? 'auto' : 'none',
    opacity: canSelectAll ? 1 : 0.4
  },
  clearAllButton: {
    pointerEvents: canClearAll ? 'auto' : 'none',
    opacity: canClearAll ? 1 : 0.4
  }
}));

const MultiselectFormField: React.FC<IMultiselectFormFieldProps> = ({
  name,
  title,
  hideError,
  placeholder,
  className,
  style,
  classes = {},
  values = [],
  error,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled,
  selectOptions = {},
  inputOnly,
  loader,
  fullWidth,
  maxTagCount,
  freeSolo = false,
  onBlurSaveNewData = false,
  material,
  injectGlobalLov,
  showSelectAll = false,
  hidden = false,
  disabledIds = []
}) => {
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: undefined
  });
  const lovs = useAppSelector((state) => state.lovs);

  const localSelectOptions: IAbstractRecord = useMemo(() => {
    if (injectGlobalLov) {
      const list: IAbstractRecord = {};
      const globalLov = lovs[injectGlobalLov.type];
      globalLov?.forEach((item) => {
        if (injectGlobalLov.display === 'Both') {
          list[item[injectGlobalLov.value]] = `${item.Symbol} - ${item.Title}`;
        } else {
          list[item[injectGlobalLov.value]] = item[injectGlobalLov.display];
        }
      });

      return list;
    }

    return selectOptions;
  }, [injectGlobalLov, lovs, selectOptions]);

  const allKeys = useMemo(
    () => Object.keys(localSelectOptions),
    [localSelectOptions]
  );
  const cleanedValues = useMemo(
    () => (values || []).filter((val) => !!localSelectOptions[val]),
    [values, localSelectOptions]
  );

  const canSelectAll = useMemo(
    () => allKeys.some((k) => !cleanedValues.includes(k)),
    [allKeys, cleanedValues]
  );
  const canClearAll = cleanedValues.length > 0;

  const { classes: inputClasses, cx } = useStyles({
    material,
    error,
    canSelectAll,
    canClearAll
  });

  const handleSelectAll = () => {
    if (disabled) return;
    onChange?.(allKeys);
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange?.([]);
  };

  const renderInput = () => (
    <div className={inputClasses.inputContainerStyles}>
      <div className={inputClasses.fieldArea}>
        <FormControl
          error={!!error}
          disabled={!!disabled}
          className={inputClasses.inputContainerStyles}
          size="small"
          variant="outlined"
        >
          <Autocomplete
            freeSolo={freeSolo}
            multiple
            limitTags={maxTagCount}
            getLimitTagsText={(props) => (
              <div className={inputClasses.chipMaxTagDesign}>+{props}</div>
            )}
            disabled={disabled}
            options={allKeys}
            filterOptions={filterOptions}
            getOptionDisabled={(option: string) => disabledIds.includes(option)}
            onChange={(event, newValue, reason) => {
              const newStrings = newValue as string[];

              if (reason === 'selectOption') {
                const addedValues = newStrings.filter(
                  (v) => !values.includes(v)
                );
                if (addedValues.length > 0) {
                  onChange?.([...values, ...addedValues]);
                }
              }
              if (reason === 'removeOption') {
                const filtered = newStrings.filter(
                  (v) => !disabledIds.includes(v)
                );
                onChange?.([
                  ...filtered,
                  ...values.filter((v) => disabledIds.includes(v))
                ]);
              }
              if (reason === 'clear') {
                onChange?.([...values.filter((v) => disabledIds.includes(v))]);
              }
            }}
            onFocus={() => onFocus()}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
              onBlurSaveNewData
                ? onBlur(event.target.value as unknown as string[])
                : onBlur([], event)
            }
            clearOnBlur={onBlurSaveNewData}
            value={cleanedValues}
            getOptionLabel={(option: string) => localSelectOptions[option]}
            disableClearable={false}
            selectOnFocus
            disableCloseOnSelect
            renderOption={(props, option: string) => (
              <li {...props} key={option}>
                <span className={inputClasses.optionDesign}>
                  {localSelectOptions[option]}
                </span>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value
                .filter((option: string) => !!localSelectOptions[option])
                .map((option: string, index: number) => {
                  const label = localSelectOptions[option];
                  const tagProps = getTagProps({ index });

                  const isDisabledChip = disabledIds.includes(option);

                  return label ? (
                    <Chip
                      label={label}
                      {...tagProps}
                      onDelete={isDisabledChip ? undefined : tagProps.onDelete}
                      className={inputClasses.chipStyle}
                      key={index}
                      sx={{
                        opacity: isDisabledChip ? 0.6 : 1,
                        pointerEvents: isDisabledChip ? 'none' : 'auto'
                      }}
                    />
                  ) : null;
                })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={values?.length > 0 ? '' : placeholder}
                className={clsx(
                  !material && inputClasses.inputStyles,
                  material && inputClasses.selectMaterial,
                  error && inputClasses.selectError
                )}
                variant="standard"
              />
            )}
            componentsProps={{
              popper: {
                placement: 'bottom',
                modifiers: [
                  { name: 'flip', enabled: true },
                  {
                    name: 'preventOverflow',
                    options: {
                      altAxis: true,
                      tether: true,
                      rootBoundary: 'viewport',
                      padding: 8
                    }
                  },
                  { name: 'offset', options: { offset: [0, 8] } }
                ]
              },
              paper: { sx: { maxHeight: 280, overflowY: 'auto' } }
            }}
          />
        </FormControl>
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </div>
      {showSelectAll && (
        <div className={inputClasses.actionsBelow}>
          <button
            type="button"
            onClick={handleSelectAll}
            className={cx(
              inputClasses.actionButton,
              inputClasses.selectAllButton
            )}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className={cx(
              inputClasses.actionButton,
              inputClasses.clearAllButton
            )}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
  if (hidden) {
    return null;
  }

  return inputOnly ? (
    <>{renderInput()}</>
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        hideError={hideError}
        className={clsx(classes.wrapper, className)}
        fullWidth={fullWidth}
        material={material}
        style={style}
      >
        {renderInput()}
      </FormFieldWrapper>
    </>
  );
};

export default MultiselectFormField;
